/**
 * 権限グループの新規作成 / 編集モーダル (権限管理 SBI-8)。
 *
 * - 新規 (`mode='create'`) は POST、編集 (`mode='edit'`) は PUT で保存する。
 * - 利用できる機能はチェックボックスで選ぶ (選択肢と表示名は `GET /features` のカタログ)。
 *   区分 (ControlPanel / 管理系) ごとにまとめて表示する。機能 0 件のグループも作れる。
 * - 入力の妥当性検証はサーバ側が正源。400 / 409 のメッセージは日本語に変換して表示し、
 *   モーダルは開いたままにして入力内容を失わない。
 *   - 原因の入力欄が特定できるもの (名称の重複など): その欄に赤枠 + 欄の直下の文で表示する。
 *   - 特定できないもの (最後の管理権限グループから管理系機能を外す 409 など): バナーに表示する。
 * - 編集の更新確定前に、他者の更新 / 削除を確認する (Issue #765、`useConfirmDialog().guardedUpdate`)。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

import type { PermissionGroupsClient } from '../api/permissionGroups';
import type { PermissionFeature, PermissionGroup, PermissionGroupSaveResult } from '../api/types';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { groupFeaturesByScope } from '../permissions/adminLabels';
import { judgeRemoteChange } from '../utils/remoteChange';

import { AdminFormModal, FieldError } from './AdminFormModal';
import {
  PERMISSION_ADMIN_REQUIRED_MESSAGES,
  describeAdminError,
  type PermissionAdminErrorField,
} from './permissionAdminMessages';

/** グループ名の最大文字数 (バックエンド `MAX_PERMISSION_GROUP_NAME_LENGTH` と同じ) */
export const MAX_PERMISSION_GROUP_NAME_LENGTH = 100;
/** 説明の最大文字数 (バックエンド `MAX_PERMISSION_GROUP_DESCRIPTION_LENGTH` と同じ) */
export const MAX_PERMISSION_GROUP_DESCRIPTION_LENGTH = 500;

interface FormState {
  name: string;
  description: string;
  features: string[];
}

type FieldErrors = Partial<Record<PermissionAdminErrorField, string>>;

/** エラー欄の走査順 (画面の上から下) */
const FIELD_ORDER: readonly PermissionAdminErrorField[] = ['name', 'description'];

function normalize(form: FormState): string {
  return JSON.stringify({
    name: form.name.trim(),
    description: form.description.trim(),
    features: [...form.features].sort(),
  });
}

export interface PermissionGroupFormModalProps {
  mode: 'create' | 'edit';
  /** 編集対象 (mode='edit' のとき必須)。新規のときは null */
  group: PermissionGroup | null;
  /** 機能カタログ (チェックボックスの選択肢) */
  features: readonly PermissionFeature[];
  apiClient: PermissionGroupsClient;
  /** 保存成功時に呼ばれる */
  onSaved: (saved: PermissionGroupSaveResult, action: '登録' | '更新') => void;
  /** 編集中に他者が削除していたとき、お知らせを閉じた後に呼ばれる */
  onDeletedByOthers: () => void;
  onClose: () => void;
}

export function PermissionGroupFormModal(props: PermissionGroupFormModalProps): React.JSX.Element {
  const { mode, group, features, apiClient, onSaved, onDeletedByOthers, onClose } = props;
  const isEditing = mode === 'edit';

  const [form, setForm] = useState<FormState>(() => ({
    name: group?.name ?? '',
    description: group?.description ?? '',
    features: group?.features ?? [],
  }));
  /** 開いたときの入力内容 (「何か変更したか」の基準) */
  const [initialKey] = useState<string>(() =>
    normalize({
      name: group?.name ?? '',
      description: group?.description ?? '',
      features: group?.features ?? [],
    }),
  );
  /** 開いたときの更新日時。保存時に他者の更新 / 削除を判定する基準 */
  const [baselineUpdatedAt] = useState<string | null>(() => group?.updated_at ?? null);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const { confirmDiscard, guardedUpdate, confirmDialog } = useConfirmDialog();
  const fieldRefs = useRef<
    Partial<Record<PermissionAdminErrorField, HTMLInputElement | HTMLTextAreaElement | null>>
  >({});

  const dirty = normalize(form) !== initialKey;

  /** 原因欄があるエラーは先頭の欄にフォーカスする。 */
  useEffect(() => {
    for (const field of FIELD_ORDER) {
      if (fieldErrors[field]) {
        fieldRefs.current[field]?.focus();
        return;
      }
    }
  }, [fieldErrors]);

  const updateForm = useCallback((patch: Partial<FormState>): void => {
    setForm((prev) => ({ ...prev, ...patch }));
    setFieldErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      if ('name' in patch) delete next.name;
      if ('description' in patch) delete next.description;
      return next;
    });
  }, []);

  const toggleFeature = useCallback(
    (key: string, checked: boolean): void => {
      const set = new Set(form.features);
      if (checked) set.add(key);
      else set.delete(key);
      // カタログの定義順で保持する (サーバも定義順で返す)
      updateForm({ features: features.map((f) => f.key).filter((k) => set.has(k)) });
    },
    [features, form.features, updateForm],
  );

  /** 実際に保存する (新規 = POST / 編集 = PUT)。入力チェックと他者の更新 / 削除の確認は通過済みの前提。 */
  const executeSave = useCallback(async (): Promise<void> => {
    setSaving(true);
    setErrorMessage(null);
    setFieldErrors({});
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      features: form.features,
    };
    try {
      if (isEditing && group) {
        onSaved(await apiClient.update(group.group_id, payload), '更新');
      } else {
        onSaved(await apiClient.create(payload), '登録');
      }
    } catch (err) {
      const info = describeAdminError(err);
      if (info.field === 'name' || info.field === 'description') {
        setFieldErrors({ [info.field]: info.message });
      } else {
        setErrorMessage(info.message);
      }
      setSaving(false);
    }
  }, [apiClient, form, group, isEditing, onSaved]);

  const handleSave = useCallback((): void => {
    if (form.name.trim() === '') {
      setFieldErrors({ name: PERMISSION_ADMIN_REQUIRED_MESSAGES.name });
      return;
    }
    if (!isEditing || !group) {
      void executeSave();
      return;
    }
    void guardedUpdate({
      hasChanges: dirty,
      fetchRemoteState: async () => {
        const res = await apiClient.list();
        return judgeRemoteChange(
          baselineUpdatedAt,
          res.items.find((g) => g.group_id === group.group_id),
        );
      },
      execute: () => void executeSave(),
      onDeleted: onDeletedByOthers,
    });
  }, [
    apiClient,
    baselineUpdatedAt,
    dirty,
    executeSave,
    form.name,
    group,
    guardedUpdate,
    isEditing,
    onDeletedByOthers,
  ]);

  /** 閉じる: 登録 / 更新せずに閉じるので破棄の確認を挟む (新規は常に、編集は変更したときだけ)。 */
  const handleClose = useCallback((): void => {
    if (!isEditing || dirty) {
      confirmDiscard(onClose);
      return;
    }
    onClose();
  }, [confirmDiscard, dirty, isEditing, onClose]);

  const selected = new Set(form.features);
  const scopes = groupFeaturesByScope(features);

  return (
    <AdminFormModal
      testIdPrefix="permission-group"
      title={isEditing ? '権限グループを編集' : '権限グループを新規作成'}
      saveLabel={isEditing ? '更新' : '登録'}
      saving={saving}
      errorMessage={errorMessage}
      onDismissError={() => setErrorMessage(null)}
      onSubmit={handleSave}
      onClose={handleClose}
      overlay={confirmDialog}
    >
      <p className="form-required-hint">
        <span className="required-mark">必須</span> がついた項目は必ず入力してください
      </p>

      <label>
        <span className="required">グループ名</span>
        <input
          type="text"
          data-testid="permission-group-name"
          value={form.name}
          maxLength={MAX_PERMISSION_GROUP_NAME_LENGTH}
          readOnly={saving}
          placeholder="例: 運行情報担当"
          ref={(el) => {
            fieldRefs.current.name = el;
          }}
          className={fieldErrors.name ? 'is-invalid' : ''}
          aria-invalid={fieldErrors.name ? true : undefined}
          aria-describedby={fieldErrors.name ? 'permission-group-name-error' : undefined}
          onChange={(e) => updateForm({ name: e.target.value })}
        />
        <FieldError id="permission-group-name-error" message={fieldErrors.name} />
        <small>
          同じ名前のグループは作れません (全角 / 半角・大文字 / 小文字の違いは区別しません)。最大
          {MAX_PERMISSION_GROUP_NAME_LENGTH}文字。
        </small>
      </label>

      <label>
        <span>説明</span>
        <textarea
          data-testid="permission-group-description"
          rows={3}
          value={form.description}
          maxLength={MAX_PERMISSION_GROUP_DESCRIPTION_LENGTH}
          readOnly={saving}
          placeholder="例: 運行情報の登録・更新を行う担当者"
          ref={(el) => {
            fieldRefs.current.description = el;
          }}
          className={fieldErrors.description ? 'is-invalid' : ''}
          aria-invalid={fieldErrors.description ? true : undefined}
          aria-describedby={
            fieldErrors.description ? 'permission-group-description-error' : undefined
          }
          onChange={(e) => updateForm({ description: e.target.value })}
        />
        <FieldError id="permission-group-description-error" message={fieldErrors.description} />
        <small>最大{MAX_PERMISSION_GROUP_DESCRIPTION_LENGTH}文字。任意です。</small>
      </label>

      <fieldset className="permission-feature-fieldset" data-testid="permission-group-features">
        <legend>利用できる機能</legend>
        {scopes.length === 0 ? (
          <p className="permission-feature-empty" data-testid="permission-group-features-empty">
            選択できる機能がありません。
          </p>
        ) : null}
        {scopes.map((scope) => (
          <div key={scope.scope} className="permission-feature-scope">
            <div className="permission-feature-scope__title">{scope.label}</div>
            <div className="route-id-checkboxes">
              {scope.features.map((feature) => (
                <label key={feature.key} className="route-id-checkboxes__item">
                  <input
                    type="checkbox"
                    data-testid={`permission-group-feature-${feature.key}`}
                    checked={selected.has(feature.key)}
                    disabled={saving}
                    onChange={(e) => toggleFeature(feature.key, e.target.checked)}
                  />
                  {feature.label}
                </label>
              ))}
            </div>
          </div>
        ))}
        <small>
          チェックした機能の画面 / API
          を、このグループのメンバーが利用できます。ユーザーが複数のグループに所属すると、利用できる機能はその合計になります。
          「権限管理 (管理者向け)」を外すと、最後の管理者がいなくなる場合は拒否されます。
        </small>
      </fieldset>
    </AdminFormModal>
  );
}
